import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useMeta } from "@/lib/AppContext";
import { ReviewChip, StatusChip, Empty } from "@/components/chips";
import * as Icons from "lucide-react";

const FOLDER_TYPES = ["email_templates", "text_templates", "action_plans", "automations"];

function AssetRow({ it, type, sid, nav, selected, onToggle }) {
  return (
    <div className="flex items-center gap-2 border-b border-stone-100 hover:bg-stone-50 transition-colors pl-3">
      <input type="checkbox" checked={selected} onChange={(e) => { e.stopPropagation(); onToggle(it.fub_id); }}
        data-testid={`row-checkbox-${it.fub_id}`} className="shrink-0 w-4 h-4 accent-amber-500" />
      <button
        data-testid={`asset-row-${it.fub_id}`}
        onClick={() => nav(`/s/${sid}/asset/${type}/${it.fub_id}`)}
        className="flex-1 text-left flex items-center justify-between gap-4 px-2 py-3"
      >
        <div className="min-w-0">
          <div className="text-stone-900 truncate" data-testid={`asset-row-label-${it.fub_id}`}>{it.badges?.label || it.name}</div>
          <div className="text-xs text-stone-400 mt-0.5 flex items-center gap-2 flex-wrap">
            {it.badges?.assigned_to && <span data-testid={`asset-row-assigned-${it.fub_id}`}>Assigned to {it.badges.assigned_to}</span>}
            {it.used_by_count > 0 && <span>{it.used_by_count} used-by</span>}
            {it.references_count > 0 && <span>· {it.references_count} refs</span>}
            {it.badges?.steps != null && <span>· {it.badges.steps} steps</span>}
            {it.badges?.count != null && <span>· {it.badges.count} people</span>}
            {it.facets?.trigger && <span>· {it.facets.trigger}</span>}
            {it.has_note && <span className="text-amber-600">· note</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {it.badges?.status && <span data-testid={`asset-row-status-${it.fub_id}`}><StatusChip value={it.badges.status} /></span>}
          <ReviewChip status={it.review_status} />
          <Icons.ChevronRight className="w-4 h-4 text-stone-300" />
        </div>
      </button>
    </div>
  );
}

function Section({ title, subtitle, count, children, testid }) {
  const [open, setOpen] = useState(false);   // collapsed by default
  return (
    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden mb-4" data-testid={testid}>
      <button onClick={() => setOpen(!open)} data-testid={`${testid}-toggle`}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-stone-50 border-b border-stone-100 hover:bg-stone-100">
        <span className="flex items-center gap-2 text-sm font-medium text-stone-700">
          <Icons.ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} />
          {title}
          {subtitle && <span className="text-xs text-stone-400 font-normal">{subtitle}</span>}
        </span>
        <span className="mono text-xs text-stone-400">{count}</span>
      </button>
      {open && children}
    </div>
  );
}

function BulkBar({ count, withNotes, states, onApply, onClear }) {
  const [status, setStatus] = useState("Review");
  const [note, setNote] = useState("");
  const [applyNote, setApplyNote] = useState(false);
  return (
    <div data-testid="bulk-bar" className="fixed bottom-0 left-64 right-0 bg-[#1a1a17] text-stone-200 px-6 py-3 flex items-center gap-3 flex-wrap z-40 border-t border-black/40">
      <span className="text-sm font-medium" data-testid="bulk-count">{count} selected</span>
      <select data-testid="bulk-status" value={status} onChange={(e) => setStatus(e.target.value)}
        className="bg-stone-800 border border-stone-600 rounded-lg text-sm px-3 py-1.5">
        {states.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <label className="flex items-center gap-1.5 text-sm">
        <input type="checkbox" checked={applyNote} onChange={(e) => setApplyNote(e.target.checked)} data-testid="bulk-apply-note" className="accent-amber-500" />
        Apply note
      </label>
      {applyNote && (
        <input data-testid="bulk-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to apply to all selected…"
          className="flex-1 min-w-[200px] bg-stone-800 border border-stone-600 rounded-lg text-sm px-3 py-1.5" />
      )}
      {applyNote && withNotes > 0 && (
        <span className="text-xs text-amber-300">{withNotes} selected already have a note</span>
      )}
      <button data-testid="bulk-apply-btn" onClick={() => onApply({ status, note, applyNote })}
        className="bg-amber-500 text-stone-900 text-sm font-medium px-4 py-1.5 rounded-lg hover:bg-amber-400">Apply</button>
      <button data-testid="bulk-clear" onClick={onClear} className="text-sm text-stone-400 hover:text-stone-200 underline">Clear</button>
    </div>
  );
}

export default function CategoryPage() {
  const { sid, type } = useParams();
  const { meta } = useMeta();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [facets, setFacets] = useState({});
  const [sort, setSort] = useState("");
  const [grouped, setGrouped] = useState(true);
  const [slGroups, setSlGroups] = useState(null);
  const [sel, setSel] = useState(new Set());

  useEffect(() => { setFacets({}); setStatus(""); setSort(""); setQ(""); setGrouped(true); setSel(new Set()); }, [type]);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    params.set("type", type);
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    if (sort) params.set("sort", sort);
    Object.entries(facets).forEach(([k, vals]) => vals.forEach((v) => params.append("facet", `${k}:${v}`)));
    return api.get(`/snapshots/${sid}/assets?${params.toString()}`).then((r) => setData(r.data));
  }, [type, q, status, sort, facets, sid]);

  useEffect(() => { setData(null); const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    if (type === "smart_lists") api.get(`/snapshots/${sid}/smart_list_groups`).then((r) => setSlGroups(r.data));
    else setSlGroups(null);
  }, [type, sid]);

  const label = meta?.type_labels?.[type] || type;
  const toggleFacet = (k, v) => setFacets((prev) => {
    const cur = new Set(prev[k] || []);
    cur.has(v) ? cur.delete(v) : cur.add(v);
    const next = { ...prev }; next[k] = [...cur]; if (!next[k].length) delete next[k];
    return next;
  });
  const activeFilterCount = Object.values(facets).reduce((n, a) => n + a.length, 0) + (status ? 1 : 0);
  const canGroupFolder = FOLDER_TYPES.includes(type);
  const canGroupCollection = type === "smart_lists";
  const showGrouped = grouped && (canGroupFolder || canGroupCollection);

  const items = data?.items || [];
  const byId = Object.fromEntries(items.map((i) => [i.fub_id, i]));

  const toggleSel = (id) => setSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = items.length > 0 && items.every((i) => sel.has(i.fub_id));
  const selectAll = () => setSel(allSelected ? new Set() : new Set(items.map((i) => i.fub_id)));

  const applyBulk = async ({ status: bstatus, note, applyNote }) => {
    const selItems = items.filter((i) => sel.has(i.fub_id));
    const withNotes = selItems.filter((i) => i.has_note).length;
    if (applyNote && note.trim() && withNotes > 0) {
      if (!window.confirm(`This will REPLACE the existing note on ${withNotes} of ${selItems.length} selected item(s). Continue?`)) return;
    }
    await api.put(`/snapshots/${sid}/reviews/bulk`, {
      type, fub_ids: selItems.map((i) => i.fub_id), status: bstatus,
      note: applyNote ? note : null, apply_note: applyNote,
    });
    setSel(new Set());
    await load();
  };

  // ---------- grouping ----------
  let groupedSections = null;
  if (showGrouped && canGroupCollection && slGroups) {
    const sections = slGroups.groups.map((g) => ({
      title: `${g.icon || ""} ${g.name || "Collection"}`.trim(),
      subtitle: g.sharedWithUsers?.length ? `shared with ${g.sharedWithUsers.join(", ")}` : (g.shared ? "shared" : "private"),
      rows: (g.members || []).map((m) => byId[m.fub_id]).filter(Boolean),
    }));
    const unorg = slGroups.unorganized.map((m) => byId[m.fub_id]).filter(Boolean);
    groupedSections = [...sections, { title: "Unorganized", subtitle: "not in any collection", rows: unorg }];
  } else if (showGrouped && canGroupFolder) {
    const folderMap = {}; const unfiled = [];
    items.forEach((it) => {
      if (!it.folders?.length) unfiled.push(it);
      else it.folders.forEach((f) => { (folderMap[f.name] = folderMap[f.name] || []).push(it); });
    });
    groupedSections = Object.keys(folderMap).sort((a, b) => a.localeCompare(b)).map((n) => ({ title: n, rows: folderMap[n] }));
    groupedSections.push({ title: "Unfiled", subtitle: "no folder", rows: unfiled });
  }

  const selWithNotes = items.filter((i) => sel.has(i.fub_id) && i.has_note).length;

  return (
    <div className="p-8 max-w-6xl pb-24" data-testid={`category-${type}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl text-stone-900">{label}</h1>
        {(canGroupFolder || canGroupCollection) && (
          <div className="flex rounded-lg border border-stone-300 overflow-hidden text-sm" data-testid="view-toggle">
            <button onClick={() => setGrouped(true)} data-testid="view-grouped"
              className={`px-3 py-1.5 ${grouped ? "bg-stone-900 text-white" : "bg-white text-stone-600"}`}>
              {canGroupCollection ? "Collections" : "Folders"}
            </button>
            <button onClick={() => setGrouped(false)} data-testid="view-flat"
              className={`px-3 py-1.5 ${!grouped ? "bg-stone-900 text-white" : "bg-white text-stone-600"}`}>Flat</button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 mt-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Icons.Search className="w-4 h-4 absolute left-3 top-2.5 text-stone-400" />
          <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${label.toLowerCase()}…`}
            className="w-full pl-9 pr-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
        </div>
        <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)}
          className="border border-stone-300 rounded-lg text-sm px-3 py-2 bg-white">
          {(data?.sort_options || [{ value: "", label: "Sort" }]).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select data-testid="status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="border border-stone-300 rounded-lg text-sm px-3 py-2 bg-white">
          <option value="">All review states</option>
          {meta?.review_states?.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={selectAll} data-testid="select-all-btn"
          className="border border-stone-300 rounded-lg text-sm px-3 py-2 bg-white hover:bg-stone-100 flex items-center gap-2">
          <Icons.CheckSquare className="w-4 h-4" /> {allSelected ? "Clear all" : `Select all${items.length ? ` (${items.length})` : ""}`}
        </button>
      </div>

      {data?.facet_options && Object.keys(data.facet_options).length > 0 && (
        <div className="mt-3 space-y-2" data-testid="facet-filters">
          {Object.entries(data.facet_options).map(([key, opt]) => (
            <div key={key} className="flex items-start gap-2 flex-wrap">
              <span className="text-xs uppercase tracking-wide text-stone-400 w-20 shrink-0 pt-1.5">{opt.label}</span>
              <div className="flex flex-wrap gap-1.5">
                {opt.values.map((v) => {
                  const on = (facets[key] || []).includes(v.value);
                  return (
                    <button key={v.value} data-testid={`facet-${key}-${v.value}`} onClick={() => toggleFacet(key, v.value)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-amber-500 border-amber-500 text-white" : "bg-white border-stone-300 text-stone-600 hover:border-amber-400"}`}>
                      {v.label} <span className={on ? "text-amber-100" : "text-stone-400"}>{v.count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {activeFilterCount > 0 && (
            <button onClick={() => { setFacets({}); setStatus(""); }} data-testid="clear-filters"
              className="text-xs text-stone-500 underline hover:text-stone-800">Clear filters ({activeFilterCount})</button>
          )}
        </div>
      )}

      <div className="mt-5">
        {!data ? (
          <div className="text-stone-400 py-8">Loading…</div>
        ) : items.length === 0 ? (
          <Empty>No {label.toLowerCase()} match.</Empty>
        ) : showGrouped && groupedSections ? (
          <>
            {canGroupFolder && <p className="text-xs text-stone-400 mb-2">Folders shown alphabetically (catalog presentation — not FUB's internal folder order). Collapsed by default.</p>}
            {canGroupCollection && slGroups && !slGroups.enriched && (
              <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                Collections appear here after internal enrichment is enabled. All Smart Lists currently show under “Unorganized”.
              </div>
            )}
            {groupedSections.filter((s) => s.rows.length).map((s, i) => (
              <Section key={i} title={s.title} subtitle={s.subtitle} count={s.rows.length} testid={`group-${i}`}>
                {s.rows.map((it) => <AssetRow key={it.fub_id} it={it} type={type} sid={sid} nav={nav} selected={sel.has(it.fub_id)} onToggle={toggleSel} />)}
              </Section>
            ))}
          </>
        ) : (
          <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
            <div className="text-xs text-stone-500 px-4 py-2 border-b border-stone-100">{data.count} items</div>
            {items.map((it) => <AssetRow key={it.fub_id} it={it} type={type} sid={sid} nav={nav} selected={sel.has(it.fub_id)} onToggle={toggleSel} />)}
          </div>
        )}
      </div>

      {sel.size > 0 && (
        <BulkBar count={sel.size} withNotes={selWithNotes} states={meta?.review_states || []}
          onApply={applyBulk} onClear={() => setSel(new Set())} />
      )}
    </div>
  );
}
